/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { useTranslation } from "react-i18next";
import { AuroraBackground } from "@/components/AuroraBackground";
import { Reveal } from "@/components/Reveal";
import { StoreBadgeGroup } from "@/components/StoreBadges";

/**
 * Shared pain-specific landing page: one pain → one promise → 3-step proof → CTA.
 * Each acquisition page (after-a-fight, reconnect, new-parents, long-distance)
 * is a thin wrapper that passes its translation namespace, icons, and bg variant.
 * Content lives under `<ns>.*` / `seo.<ns>.*` in public/locales/site/*.json.
 */
export const PainLandingPage = ({ ns, icons = [], variant = "soft", photo }) => {
  const { t } = useTranslation("site");
  const tk = (k, fb) => t(`${ns}.${k}`, fb);
  const cardBg = ["var(--aurora-emotional)", "var(--aurora-journeys)", "var(--aurora-coach)"];

  return (
    <>
      <section className="relative pb-12">
        <AuroraBackground variant={variant} />
        <div className="relative mx-auto max-w-3xl px-5 lg:px-8 pt-10 lg:pt-16 text-center">
          <Reveal>
            <p className="aurora-chip mb-6 inline-flex"><span className="size-1.5 rounded-full bg-[var(--aurora-gold)]" />{tk("kicker")}</p>
            <h1 className="font-serif text-[2.6rem] sm:text-5xl lg:text-[4.2rem] tracking-tight font-light leading-[1.05]">
              {tk("title1")}{" "}<em className="italic aurora-shimmer-text">{tk("title2")}</em>{" "}{tk("title3")}
            </h1>
            <p className="mt-6 text-white/75 text-lg font-light max-w-xl mx-auto">{tk("subtitle")}</p>
            <div className="mt-8 flex flex-col items-center">
              <StoreBadgeGroup startLabel={tk("cta", t("common.startFree"))} />
              <p className="mt-3 text-xs text-white/45 tracking-wide">{t("home.freeBadge")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative py-16">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 grid md:grid-cols-3 gap-5">
          {[1, 2, 3].map((n, i) => (
            <Reveal key={n} delay={i * 80}>
              <div className="aurora-card p-7 h-full" style={{ background: cardBg[i] }}>
                <span className="size-11 rounded-2xl bg-white/8 flex items-center justify-center text-[var(--aurora-gold)] mb-5">
                  {icons[i] || null}
                </span>
                <h3 className="font-serif text-2xl">{tk(`card${n}T`)}</h3>
                <p className="mt-3 text-white/75 font-light leading-relaxed">{tk(`card${n}B`)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className={`relative py-24 sm:py-32 ${photo ? "photo-interlude" : ""}`}>
        {photo ? (
          <>
            <img
              src={photo}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="photo-interlude__img"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="photo-interlude__tint" />
            <div className="grain" />
          </>
        ) : null}
        <div className="relative mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <Reveal>
            <blockquote className="font-serif italic text-2xl sm:text-3xl text-white/90 leading-relaxed">
              "{tk("quote")}"
              <span className="mt-3 block not-italic text-sm tracking-wide text-white/50">— {tk("quoteSource")}</span>
            </blockquote>
            <p className="mt-10 text-white/80 font-light">{tk("closingBody")}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <StoreBadgeGroup startLabel={t("common.startFree")} />
            </div>
            <p className="mt-4 text-xs text-white/50 tracking-wide">{t("common.freeFooter")}</p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default PainLandingPage;
